import { ApiError } from '@/lib/api/errors';
import { mutationErrorTone, type ErrorToastTone } from '@/lib/api/mutation-toast';

/**
 * The shapefile import's failure vocabulary — the one place in the app where a toast's tone is
 * keyed on an error **code** rather than an HTTP status
 * ([ADR-0025](../../../docs/adr/0025-import-toast-tone-by-error-code.md)).
 *
 * Pure and DOM-free on purpose: this is what the ADR's protecting assertion runs against, in the
 * fast `node` test project (see `vitest.config.ts`'s `NODE_TEST_GLOBS`).
 */

/**
 * The server's own `MAX_IMPORT_FILE_BYTES`. **Inclusive** — exactly 5,242,880 bytes is accepted,
 * because the backend's interceptor is deliberately written as `MAX_IMPORT_FILE_BYTES + 1` with a
 * comment forbidding its "simplification". Mirroring the boundary is the point of mirroring the
 * constant.
 */
export const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;

/** Verbatim from 113609 and Figma `783:660341`, and identical to the server's 413 message, so the
 *  client pre-check and the server refusal read as one thing. */
export const IMPORT_FILE_TOO_LARGE_MESSAGE = 'File size exceeds 5 MB.';

/**
 * The only thing the client judges about a chosen file. `File.size` is exact and the check is
 * instant, which spares the Grower an upload multer will abort mid-stream. **Nothing else is
 * pre-checked** — not the ZIP magic bytes, not the MIME type, not members, geometry or Point count:
 * all of those need unzipping, which
 * [ADR-0018](../../../docs/adr/0018-field-boundary-geometry-and-drawing.md) forbids, and the server
 * returns a distinct code for each.
 *
 * `>`, never `>=`. See {@link MAX_IMPORT_FILE_BYTES}.
 */
export function exceedsImportSizeLimit(bytes: number): boolean {
  return bytes > MAX_IMPORT_FILE_BYTES;
}

/**
 * ADR-0025's map: exactly five codes, closed by construction. `INVALID_FORMAT` (400) and
 * `NO_POLYGON` (422) deliberately share the sentence "Invalid file format." — the backend keeps the
 * codes apart only so "not a shapefile" and "a shapefile of the wrong kind" stay distinguishable in
 * logs, because the Grower's remedy is the same — so they must share a tone too. That equality is
 * why this map exists.
 */
const TONE_BY_IMPORT_CODE: Record<string, ErrorToastTone> = {
  FIELD_IMPORT_INVALID_FORMAT: 'error',
  FIELD_IMPORT_NO_POLYGON: 'error',
  FIELD_IMPORT_TOO_MANY_POINTS: 'error',
  FIELD_IMPORT_MISSING_FILES: 'warning',
  FIELD_IMPORT_FILE_TOO_LARGE: 'warning',
};

/**
 * Which tone one import failure gets.
 *
 * **Anything not in the map falls through to `mutationErrorTone`** — a 401, a 5xx or a dropped
 * connection on this call is toned by the app-wide rule, so the divergence cannot spread beyond the
 * five named codes. `mutationErrorTone` itself is untouched and no general `meta.errorTone` seam is
 * added (ADR-0025's two rejected alternatives).
 */
export function importErrorTone(error: unknown): ErrorToastTone {
  const code = error instanceof ApiError ? error.code : undefined;
  return (code ? TONE_BY_IMPORT_CODE[code] : undefined) ?? mutationErrorTone(error);
}
